import { createRequestGuard } from "~/common/requestGuard"
import { roundToDecimals } from "~/common/utils"
import { createHotVideoPromptSaver } from "~/common/tasks/hotVideoPromptSave"
import { buildRecreateProductInfo } from "~/common/video/recreateProductInfo"
import {
  buildStoryboardPromptText,
  buildStoryboardPromptTextFromSource,
  parseStoryboardPayload,
} from "~/common/video/storyboard"
import {
  getStoryboardGenerationOptions,
  type VideoOptions,
} from "~/common/constants"
import { createVideoChannelFrontendRemark } from "~/common/tasks/taskFrontendRemark"
import {
  getRecreateReferenceChanges,
  getRecreateImageUrls,
} from "~/common/video/recreate"
import {
  saveHotVideoOptimizePrompt,
  updateHotVideoFrontendRemark,
} from "~/api/task"

import type { HotVideoState } from "./types"

export const saveHotVideoChannel = (
  taskId: string,
  channel: API.Task.CreateVideoChannel,
) => {
  return updateHotVideoFrontendRemark({
    frontend_remark: createVideoChannelFrontendRemark(channel),
    task_id: taskId,
  })
}

export const getReferenceDefaults = (): Pick<
  HotVideoState,
  | "start_time"
  | "end_time"
  | "referenceModel"
  | "modelDrafts"
  | "referenceAudio"
  | "resourcesPending"
> => ({
  start_time: 0,
  end_time: 0,
  referenceModel: { mode: "none" },
  modelDrafts: {},
  referenceAudio: null,
  resourcesPending: false,
})

export const getCustomModel = (
  state: Pick<HotVideoState, "referenceModel" | "modelDrafts">,
) =>
  state.referenceModel.mode === "custom"
    ? state.referenceModel
    : state.modelDrafts.custom

export const requestGuard = createRequestGuard()
export const artlineRequestGuard = createRequestGuard()
export const productRequestGuard = createRequestGuard()
export const promptSaver = createHotVideoPromptSaver({
  save: async (params) => {
    const response = await saveHotVideoOptimizePrompt(params, {
      notify: false,
    })
    if (response.data?.optimize_prompt?.result_prompt !== params.result_prompt)
      throw new Error("未确认脚本保存结果，请重试")
  },
  onChange: (scriptSaveStatus, scriptSaveError) => {
    updateScriptSaveState?.({ scriptSaveStatus, scriptSaveError })
  },
})

// promptSaver 先于 store 创建，由 store 创建后绑定状态写回，避免模块循环依赖
let updateScriptSaveState:
  | ((
      state: Pick<HotVideoState, "scriptSaveStatus" | "scriptSaveError">,
    ) => void)
  | undefined

export const bindScriptSaveState = (
  update: NonNullable<typeof updateScriptSaveState>,
) => {
  updateScriptSaveState = update
}

export const getPromptState = (replicaPrompt = "") => {
  const payload = parseStoryboardPayload(replicaPrompt)
  const rows = payload?.rows || []
  for (const row of rows) {
    row.start_time = roundToDecimals(row.start_time)
    row.end_time = roundToDecimals(row.end_time)
    row.duration = Math.max(0, roundToDecimals(row.end_time - row.start_time))
  }
  return {
    replicaPrompt: payload
      ? buildStoryboardPromptTextFromSource(replicaPrompt, rows) ||
        buildStoryboardPromptText(payload, rows)
      : replicaPrompt,
    replicaStoryboardRows: rows,
    rawModelOutput: payload ? "" : replicaPrompt,
  }
}

export const getInputFingerprint = (
  state: Pick<HotVideoState, "videoImageUrls" | "videoUrl" | "productInfo">,
) =>
  JSON.stringify({
    imageUrls: getRecreateImageUrls(state.videoImageUrls),
    sourceVideoUrl: state.videoUrl.trim(),
    productInfo: buildRecreateProductInfo(state.productInfo),
  })

export const isHotVideoPromptStale = (state: HotVideoState): boolean =>
  Boolean(
    state.replicaPrompt.trim() &&
    ((state.replicaPromptInputFingerprint &&
      state.replicaPromptInputFingerprint !== getInputFingerprint(state)) ||
      getRecreateReferenceChanges(state).length > 0),
  )

export const getHotVideoGenerationOptions = (
  state: Pick<HotVideoState, "videoOptions" | "replicaStoryboardRows">,
): VideoOptions => {
  return getStoryboardGenerationOptions(
    state.videoOptions,
    state.replicaStoryboardRows.at(-1)?.end_time,
  )
}

export const canUseHotVideoOriginalAudio = (state: HotVideoState): boolean =>
  !state.staffPreview ||
  Boolean(
    state.staffPreview.parse_video.audio_url &&
    state.staffPreview.parse_video.video_url === state.videoUrl.trim(),
  )
